import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { Report } from '../../types/types';
import type { HighlightedGraphNodeState, LineageInfoPanelState } from './types';

export type LineageGraphContextValue = {
  highlightedNode: HighlightedGraphNodeState | undefined;
  setHighlightedNode: (node: HighlightedGraphNodeState | undefined) => void;
  exceededColumnLimitAssets: Set<string>;
  setExceededColumnLimitAssets: (ids: Set<string>) => void;
  lineageInfoPanel: LineageInfoPanelState;
  setLineageInfoPanel: (state: LineageInfoPanelState) => void;
  /** Report JSON (lineage page); absent in controlled contexts (e.g. incident panel). */
  reportData?: Report;
  /** True when props override URL / tree selection (e.g. incident panel). */
  isControlled: boolean;
};

const LineageGraphContext = createContext<LineageGraphContextValue | null>(
  null,
);

type ProviderProps = {
  children: ReactNode;
  /** Initial exceeded-column parent ids (e.g. from lineage API). */
  initialExceededColumnLimitAssetIds?: string[];
  reportData?: Report;
  isControlled?: boolean;
};

export const LineageGraphProvider = ({
  children,
  initialExceededColumnLimitAssetIds,
  reportData,
  isControlled = false,
}: ProviderProps) => {
  const [highlightedNode, setHighlightedNode] = useState<
    HighlightedGraphNodeState | undefined
  >(undefined);
  const [exceededColumnLimitAssets, setExceededColumnLimitAssets] = useState<
    Set<string>
  >(() => new Set(initialExceededColumnLimitAssetIds));
  const [lineageInfoPanel, setLineageInfoPanel] =
    useState<LineageInfoPanelState>({ isOpen: false });

  const serializedInitial = JSON.stringify(
    initialExceededColumnLimitAssetIds ?? [],
  );
  useEffect(() => {
    setExceededColumnLimitAssets(
      new Set(JSON.parse(serializedInitial) as string[]),
    );
  }, [serializedInitial]);

  const value = useMemo(
    () => ({
      highlightedNode,
      setHighlightedNode,
      exceededColumnLimitAssets,
      setExceededColumnLimitAssets,
      lineageInfoPanel,
      setLineageInfoPanel,
      reportData,
      isControlled,
    }),
    [
      highlightedNode,
      exceededColumnLimitAssets,
      lineageInfoPanel,
      reportData,
      isControlled,
    ],
  );

  return (
    <LineageGraphContext.Provider value={value}>
      {children}
    </LineageGraphContext.Provider>
  );
};

export const useLineageGraphContext = (): LineageGraphContextValue => {
  const ctx = useContext(LineageGraphContext);
  if (!ctx) {
    throw new Error(
      'useLineageGraphContext must be used within LineageGraphProvider',
    );
  }
  return ctx;
};
